/**
 * Handle upload errors with user-friendly messages
 */
export const handleUploadError = (error, toast) => {
  if (error.response?.status === 413) {
    const message = "Files are too large for upload. Please try:\n• Compressing files using ZIP\n• Reducing image quality\n• Splitting large files\n• Removing unnecessary files";
    toast.error(message, { duration: 8000 });
    return {
      type: 'file_size',
      message: 'Files exceed server upload limit',
      suggestions: [
        'Compress files using ZIP or 7-Zip',
        'Reduce image quality or resolution',
        'Split large files into smaller parts',
        'Remove unnecessary files from upload'
      ]
    };
  }
  
  if (error.response?.status === 400) {
    return {
      type: 'validation',
      message: error.response.data?.message || 'Invalid file format or content',
      suggestions: [
        'Check file formats are allowed',
        'Ensure files are not corrupted',
        'Verify file extensions match content'
      ]
    };
  }
  
  if (error.code === 'NETWORK_ERROR' || !error.response) {
    return {
      type: 'network',
      message: 'Network connection issue',
      suggestions: [
        'Check your internet connection',
        'Try again in a few moments',
        'Contact support if problem persists'
      ]
    };
  }
  
  return {
    type: 'general',
    message: error.message || 'Upload failed',
    suggestions: ['Please try again or contact support']
  };
};

/**
 * Format file size for display
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Check if files exceed size limits
 */
export const validateFileSizes = (files, limits = {}) => {
  const defaultLimits = {
    malware_sample: 50 * 1024 * 1024, // 50MB
    additional_files: 10 * 1024 * 1024, // 10MB
    general: 10 * 1024 * 1024 // 10MB
  };
  
  const fileLimits = { ...defaultLimits, ...limits };
  const errors = [];
  
  Object.entries(files).forEach(([fieldName, fileList]) => {
    if (!fileList || fileList.length === 0) return;
    
    const limit = fileLimits[fieldName] || fileLimits.general;
    
    Array.from(fileList).forEach(file => {
      if (file.size > limit) {
        errors.push({
          field: fieldName,
          file: file.name,
          size: formatFileSize(file.size),
          limit: formatFileSize(limit),
          message: `${file.name} (${formatFileSize(file.size)}) exceeds ${formatFileSize(limit)} limit`
        });
      }
    });
  });
  
  return errors;
};